import { Button, message, Space } from 'antd';
import { useState } from 'react';
import { useQueryClient } from 'react-query';

import useAPIMutation from '../api/useAPIMutation';
import useAPIQuery from '../api/useAPIQuery';
import useInfiniteAPIQuery from '../api/useInfiniteAPIQuery';
import Form from '../components/Form';
import GeneralJobPostingForm, {
  GeneralJobPostingFormValues,
} from '../components/GeneralJobPostingForm';
import PageContent from '../components/PageContent';
import useAllowedUsersMutation from '../hooks/useAllowedUsersMutation';
import useCreateJobPostingMutation from '../hooks/useCreateJobPostingMutation';
import useDiscardChangesModal from '../hooks/useDiscardChangesModal';
import usePermissionCheckerQuery from '../hooks/usePermissionCheckerQuery';
import usePrefetchPages from '../hooks/usePrefetchPages';
import { useSeniorityLevels } from '../hooks/useSeniorityLevels';
import useTypedNavigate from '../hooks/useTypedNavigate';
import useTypedParams from '../hooks/useTypedParams';
import { withRouteChange } from '../services/APM';
import { mapVixtingCompany, mapVixtingDepartment, mapVixtingJobRole } from '../utils';
import { ConfirmSelfRemoveModal } from './EditOpportunity/AllowedUsersForm';

const STALE_TIME = 1000 * 60 * 5;

const CreateJobPosting = () => {
  const { tenant } = useTypedParams({ tenant: String });
  const navigate = useTypedNavigate();
  const queryClient = useQueryClient();
  //#region Form watch
  const [form] = Form.useForm();
  const [setIsDirty] = useDiscardChangesModal(form);
  const selectedCompany: string = Form.useWatch('sankhyaCompany', form);
  const selectedJobRole: string = Form.useWatch('sankhyaJobRole', form);
  const recruiterValue = Form.useWatch('recruiter', form);
  const managerValue = Form.useWatch('manager', form);
  const relatedValue = Form.useWatch('related', form);
  //#endregion
  //#region Allowed users
  const [confirmSelfRemoveModalIsOpen, setConfirmSelfRemoveModalIsOpen] = useState<boolean>(false);
  const [selfRemove, setSelfRemove] = useState(false);
  //#endregion

  const config = useAPIQuery('/configs/tenant-configurations/');

  const { seniorityLevels, defaultSelectedSeniorityLevelID } = useSeniorityLevels();

  const sankhyaCompaniesPages = useInfiniteAPIQuery(
    [
      '/sankhya/companies/',
      {
        params: {
          offsetPage: '0',
          fieldsetList: 'CODEMP',
          joinFieldsetList: 'NOMEFANTASIA',
          sort: 'CODEMP',
        },
      },
    ],
    {
      enabled: !!config.data?.enableSankhyaAdmission,
      pageKey: 'offsetPage',
    }
  );
  const sankhyaCompanies = usePrefetchPages(sankhyaCompaniesPages, (data) => data.results);

  const sankhyaJobRolesPages = useInfiniteAPIQuery(
    [
      '/sankhya/job-roles/',
      {
        params: {
          fieldsetList: 'CODCARGO,DESCRCARGO',
          filter: {
            ATIVO: 'S',
          },
          sort: 'CODCARGO',
        },
      },
    ],
    {
      enabled: !!config.data?.enableSankhyaAdmission,
      pageKey: 'offsetPage',
    }
  );
  const sankhyaJobRoles = usePrefetchPages(sankhyaJobRolesPages, (data) => data.results);

  const sankhyaDepartmentsPages = useInfiniteAPIQuery(
    [
      '/sankhya/departments/',
      {
        params: {
          fieldsetList: 'CODDEP,DESCRDEP',
          filter: {
            ATIVO: 'S',
            ANALITICO: 'S',
          },
          sort: 'CODDEP',
        },
      },
    ],
    {
      enabled: !!config.data?.enableSankhyaAdmission,
      pageKey: 'offsetPage',
    }
  );
  const sankhyaDepartments = usePrefetchPages(sankhyaDepartmentsPages, (data) => data.results);

  const vixtingCompaniesPages = useInfiniteAPIQuery(
    [
      '/vixting/vixting-companies/',
      {
        params: {
          is_active: true,
        },
      },
    ],
    {
      enabled: !!config.data?.enableVixtingAdmission,
      onError: () => {
        message.error('Falha ao carregar empresas da vixting!');
      },
    }
  );
  const vixtingCompanies = usePrefetchPages(vixtingCompaniesPages, (data) => data.results);

  const vixtingAffiliatesPages = useInfiniteAPIQuery(
    [
      '/vixting/affiliates/',
      {
        params: {
          company_id: selectedCompany,
        },
      },
    ],
    {
      enabled: !!config.data?.enableVixtingAdmission && !!selectedCompany,
      staleTime: STALE_TIME,
      onError: () => {
        message.error('Falha ao carregar filiais da vixting!');
      },
      pageKey: 'offsetPage',
    }
  );

  const vixtingAffiliates = usePrefetchPages(vixtingAffiliatesPages, (data) => data.results);

  const vixtingJobRolesPages = useInfiniteAPIQuery(
    [
      '/vixting/job-roles/',
      {
        params: {
          company_id: selectedCompany,
        },
      },
    ],
    {
      enabled: !!config.data?.enableVixtingAdmission && !!selectedCompany,
      staleTime: STALE_TIME,
      onError: () => {
        message.error('Falha ao carregar cargos da vixting!');
      },
      pageKey: 'offsetPage',
    }
  );
  const vixtingJobRoles = usePrefetchPages(vixtingJobRolesPages, (data) => data.results);

  const selectedJob = vixtingJobRoles.find(
    (jobRole) => jobRole.id?.toString() === selectedJobRole?.toString()
  );

  const vixtingUnitBusinessesPages = useInfiniteAPIQuery(
    [
      '/vixting/unit-businesses/',
      {
        params: {
          company_id: selectedCompany,
        },
      },
    ],
    {
      enabled:
        !!config.data?.enableVixtingAdmission &&
        !!config.data?.enableVixtingUnitBusiness &&
        !!selectedCompany,
      staleTime: STALE_TIME,
      onError: () => {
        message.error('Falha ao carregar unidades de negócio da vixting!');
      },
      pageKey: 'offsetPage',
    }
  );
  const vixtingUnitBusinesses = usePrefetchPages(
    vixtingUnitBusinessesPages,
    (data) => data.results
  );

  const vixtingDepartmentsPages = useInfiniteAPIQuery(
    [
      '/vixting/departments/',
      {
        params: {
          company_id: selectedCompany,
        },
      },
    ],
    {
      enabled:
        !!config.data?.enableVixtingAdmission &&
        !!selectedCompany &&
        !!selectedJob?.linkToAllDepartment,
      staleTime: STALE_TIME,
      onError: () => {
        message.error('Falha ao carregar departamentos da vixting!');
      },
      pageKey: 'offsetPage',
    }
  );
  const vixtingAllDepartments = usePrefetchPages(vixtingDepartmentsPages, (data) => data.results);

  const createJobPosting = useCreateJobPostingMutation();

  const createRecruiters = useAllowedUsersMutation();

  const mutation = useAPIMutation(
    async (variables: GeneralJobPostingFormValues, api) => {
      const jobPosting = await createJobPosting.mutateAsync({
        name: variables.name,
        positions: variables.positions,
        startSalaryRange:
          typeof variables.startSalaryRange === 'number' ? variables.startSalaryRange : undefined,
        endSalaryRange:
          typeof variables.endSalaryRange === 'number' ? variables.endSalaryRange : undefined,
        limitDays: variables.limitDays,
        seniorityLevel: variables.seniorityLevel ?? undefined,
        workModel: variables.workModel,
        hireModel: variables.hireModel,
      });

      await createRecruiters.mutateAsync({
        recruiter: variables.recruiter,
        manager: variables.manager,
        related: variables.related,
        observers: variables.observers,
        jobPostingId: jobPosting.data.id,
      });

      const affiliateData = config.data?.enableVixtingAdmission
        ? { affiliate: variables.affiliate || variables.sankhyaCompany }
        : {};

      const unitBusinessData =
        config.data?.enableVixtingAdmission && config.data?.enableVixtingUnitBusiness
          ? { unit_business: variables.unitBusiness || '' }
          : {};

      if (config.data?.enableVixtingAdmission) {
        await api.post('/vixting/vixting-job-postings/', {
          job_posting: jobPosting.data.id,
          company: variables.sankhyaCompany,
          job_role: variables.sankhyaJobRole,
          department: variables.sankhyaDepartment,
          ...affiliateData,
          ...unitBusinessData,
        });
      }

      if (config.data?.enableSankhyaAdmission) {
        await api.post(`/sankhya/sankhya-job-postings/`, {
          job_posting: jobPosting.data.id,
          company: variables.sankhyaCompany,
          job_role: variables.sankhyaJobRole,
          department: variables.sankhyaDepartment,
        });
      }

      return jobPosting;
    },
    {
      onSettled: () => {
        queryClient.invalidateQueries('/job-postings/');
        queryClient.invalidateQueries('/recruiters/');
      },
      onSuccess: (response) => {
        setIsDirty(false);
        // dispatch setIsDirty possui um delay para atualizar o estado do Modal de descarte, resolvido com uma mensagem antes do navigate
        message.success('Vaga criada');

        navigate({
          pathname: '/:tenant/opportunities/:id/change/profile/',
          params: { tenant, id: response.data.id, new: 1 },
        });
      },
    }
  );

  const usersPages = useInfiniteAPIQuery([
    '/users/',
    {
      params: {
        is_active: true,
      },
    },
  ]);
  const users = usePrefetchPages(usersPages, (data) => data.results);

  const user = useAPIQuery('/users/me/');

  const viewAllVacanciesPermission = usePermissionCheckerQuery(['view_all_vacancies']);

  const formArgs = {
    enableIntegrationAdmission:
      config.data?.enableSankhyaAdmission || config.data?.enableVixtingAdmission,
    sankhyaCompanies: config.data?.enableVixtingAdmission
      ? mapVixtingCompany(vixtingCompanies)
      : sankhyaCompanies,
    sankhyaJobRoles: config.data?.enableVixtingAdmission
      ? mapVixtingJobRole(vixtingJobRoles)
      : sankhyaJobRoles,
    sankhyaDepartments: config.data?.enableVixtingAdmission
      ? mapVixtingDepartment(
          selectedJob?.linkToAllDepartment ? vixtingAllDepartments : selectedJob?.departments || []
        )
      : sankhyaDepartments,
    affiliates: config.data?.enableVixtingAdmission ? vixtingAffiliates : [],
    unitBusinesses: config.data?.enableVixtingAdmission ? vixtingUnitBusinesses : [],
    seniority_levels: seniorityLevels,
    recruiterUserOptions: users?.filter((user) => user.is_active && !user.is_staff),
    managerUserOptions: users?.filter((user) => user.is_active && !user.is_staff),
    relatedUserOptions: users?.filter((user) => user.is_active && !user.is_staff),
    observersUserOptions: users?.filter((user) => user.is_active && !user.is_staff),
    user: user.data,
  };

  return (
    <PageContent>
      <ConfirmSelfRemoveModal
        key="ConfirmSelfRemove"
        open={confirmSelfRemoveModalIsOpen}
        okButtonProps={{ danger: true, type: 'default' }}
        onOk={() => {
          setConfirmSelfRemoveModalIsOpen(false);
          setSelfRemove(true);
          form.submit();
        }}
        onCancel={() => {
          setConfirmSelfRemoveModalIsOpen(false);
          setIsDirty(form.isFieldsTouched());
        }}
        onSuccess={() => {
          setConfirmSelfRemoveModalIsOpen(false);
        }}
        destroyOnClose
      />
      <Form<GeneralJobPostingFormValues>
        form={form}
        name="basic"
        layout="vertical"
        validateMessages={{
          required: 'Campo obrigatório.',
        }}
        onFinish={(variables) => {
          if (
            !selfRemove &&
            !viewAllVacanciesPermission.data &&
            ![recruiterValue, managerValue, ...relatedValue].includes(user.data?.id)
          ) {
            setConfirmSelfRemoveModalIsOpen(true);
          } else {
            mutation.mutate({ ...variables });
          }
        }}
        onValuesChange={(changedValues, values) => {
          if (changedValues.recruiter) {
            form.setFieldsValue({
              ...values,
              manager: changedValues.recruiter === values.manager ? undefined : values.manager,
              related: values.related?.filter((userId) => userId !== changedValues.recruiter),
              observers: values.observers?.filter((userId) => userId !== changedValues.recruiter),
            });
          }
          if (changedValues.manager) {
            form.setFieldsValue({
              ...values,
              related: values.related?.filter((userId) => userId !== changedValues.manager),
              observers: values.observers?.filter((userId) => userId !== changedValues.manager),
            });
          }
          if (changedValues.related) {
            form.setFieldsValue({
              observers: values.observers?.filter(
                (userId) => !changedValues.related.includes(userId)
              ),
            });
          }
          setIsDirty(form.isFieldsTouched());
        }}
        initialValues={{
          seniorityLevel: defaultSelectedSeniorityLevelID,
          recruiter: user.data?.is_staff ? undefined : user.data?.id,
          related: [],
          observers: [],
          positions: 1,
          limitDays: 30,
        }}
      >
        <Space direction="vertical" size={16}>
          <GeneralJobPostingForm {...formArgs} />
          <Form.Footer>
            <Form.FooterEnd>
              <Button
                onClick={() =>
                  navigate({ pathname: '/:tenant/opportunities/', params: { tenant } })
                }
              >
                Cancelar
              </Button>
              <Button loading={mutation.isLoading} type="primary" htmlType="submit">
                Salvar e Continuar
              </Button>
            </Form.FooterEnd>
          </Form.Footer>
        </Space>
      </Form>
      <div style={{ height: 80 }} />
    </PageContent>
  );
};

export default withRouteChange()(CreateJobPosting);
